/**
 * Resolves the Looker host URL and environment for the current runtime.
 *
 * Priority:
 *  1. `window.LOOKER_CONFIG.hostUrl` (Angular host).
 *  2. Build-time env var `REACT_APP_LOOKER_HOST_URL`.
 *  3. Detection from `window.location`.
 *  4. Production fallback.
 */

import type { LookerWindowConfig } from '../types/windowConfig';

export type Environment = 'dev' | 'stg' | 'prod' | 'localhost';

export interface LookerConfig {
  hostUrl: string;
  environment: Environment;
}

const LOOKER_URLS: Record<Environment, string> = {
  dev: 'https://looker.dev.pulsepoint.com',
  stg: 'https://looker.stg.pulsepoint.com',
  prod: 'https://looker.pulsepoint.com',
  localhost: 'http://localhost:9999',
};

export function getEnvironmentFromUrl(url: string | undefined): Environment {
  if (!url) return 'prod';

  const u = url.toLowerCase();

  if (u.includes('localhost') || u.includes('127.0.0.1')) return 'localhost';
  if (u.includes('.dev.')) return 'dev';
  if (u.includes('.stg.') || u.includes('staging')) return 'stg';

  return 'prod';
}

export function mapEnvironmentToBackendFormat(env: Environment): 'development' | 'staging' | 'production' {
  const mapping: Record<Environment, 'development' | 'staging' | 'production'> = {
    dev: 'development',
    stg: 'staging',
    prod: 'production',
    localhost: 'development',
  };
  return mapping[env];
}

export function getLookerConfig(): LookerConfig {
  const winCfg = typeof window !== 'undefined' ? window.LOOKER_CONFIG : undefined;

  if (winCfg?.hostUrl) {
    return { hostUrl: winCfg.hostUrl, environment: getEnvironmentFromUrl(winCfg.hostUrl) };
  }

  const envHostUrl = process.env.REACT_APP_LOOKER_HOST_URL;
  if (envHostUrl) {
    return { hostUrl: envHostUrl, environment: getEnvironmentFromUrl(envHostUrl) };
  }

  if (typeof window !== 'undefined') {
    const env = getEnvironmentFromUrl(window.location.href);
    return { hostUrl: LOOKER_URLS[env], environment: env };
  }

  return { hostUrl: LOOKER_URLS.prod, environment: 'prod' };
}

export function getUserConfigFromWindow(): LookerWindowConfig['user'] | null {
  if (typeof window === 'undefined') return null;
  return window.LOOKER_CONFIG?.user ?? null;
}

/**
 * Resolves the Looker host URL provided by the Angular host via
 * `window.LOOKER_CONFIG.hostUrl`, falling back to env / detection.
 */
export function getLookerHostUrl(): string {
  return getLookerConfig().hostUrl;
}
